import React, { useState, useEffect } from 'react';

import { getFirestore, collection, addDoc, getDocs, query, orderBy, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import Papa from 'papaparse';
import { 
  FileDown, 
  FileUp, 
  FileText, 
  Edit2, 
  Trash2, 
  DollarSign, 
  Calendar, 
  FileInput,
  Plus,
  Save,
  Receipt
} from 'lucide-react';

function Ads() {
  const [ads, setAds] = useState([]);
  const [newAd, setNewAd] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    note: ''
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const db = getFirestore();

  useEffect(() => {
    fetchAds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    calculateTotalAmount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ads]);

  const fetchAds = async () => {
    try {
      const q = query(collection(db, 'Ads'), orderBy('date', 'desc'));
      const querySnapshot = await getDocs(q);
      const adsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAds(adsList);
    } catch (error) {
      console.error("Error fetching ads: ", error);
      setError("Failed to fetch ads. Please try again.");
    }
  };
   // Add new export function
   const exportAllToCsv = () => {
    try {
      const csvData = ads.map(ad => ({
        date: ad.date,
        amount: ad.amount,
        note: ad.note
      }));

      const csv = Papa.unparse(csvData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'ads_export.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setSuccess('Successfully exported ads data to CSV!');
    } catch (error) {
      console.error("Error exporting data:", error);
      setError('Failed to export data. Please try again.');
    }
  };

  // Add new download sample function
  const downloadCsvSample = () => {
    const sampleData = [
      {
        date: new Date().toISOString().split('T')[0],
        amount: '100',
        note: 'Sample Ad Expense'
      }
    ];
    const csv = Papa.unparse(sampleData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'ads_sample.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Add new bulk import function
  const handleBulkImport = (event) => {
    const file = event.target.files[0];
    if (file) {
      setIsUploading(true);
      setError(null);
      setSuccess(null);

      Papa.parse(file, {
        header: true,
        complete: async (results) => {
          try {
            let successCount = 0;
            let errorCount = 0;

            for (const row of results.data) {
              if (row.date && row.amount) {
                try {
                  await addDoc(collection(db, 'Ads'), {
                    date: row.date,
                    amount: parseFloat(row.amount),
                    note: row.note || '',
                    createdAt: new Date()
                  });
                  successCount++;
                } catch (error) {
                  errorCount++;
                  console.error("Error adding row:", error);
                }
              }
            }

            if (successCount > 0) {
              setSuccess(`Successfully imported ${successCount} ads${errorCount > 0 ? ` (${errorCount} failed)` : ''}`);
              fetchAds();
            } else {
              setError('No valid records found in the CSV file');
            }
          } catch (error) {
            console.error("Error processing CSV:", error);
            setError('Failed to process CSV file. Please check the format and try again.');
          }
          setIsUploading(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
        },
        error: (error) => {
          console.error("Error parsing CSV:", error);
          setError('Failed to parse CSV file. Please check the format and try again.');
          setIsUploading(false);
        }
      });
    }
  };

  const calculateTotalAmount = () => {
    const total = ads.reduce((sum, ad) => sum + (ad.amount || 0), 0);
    setTotalAmount(total);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewAd(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    try {
      if (editingId) {
        await updateDoc(doc(db, 'Ads', editingId), {
          ...newAd,
          amount: parseFloat(newAd.amount),
          updatedAt: new Date()
        });
        console.log("Ad invoice updated with ID: ", editingId);
        setSuccess("Ad invoice updated successfully!");
        setEditingId(null);
      } else {
        const docRef = await addDoc(collection(db, 'Ads'), {
          ...newAd,
          amount: parseFloat(newAd.amount),
          createdAt: new Date()
        });
        console.log("Ad invoice added with ID: ", docRef.id);
        setSuccess("Ad invoice added successfully!");
      }
      setNewAd({
        date: new Date().toISOString().split('T')[0],
        amount: '',
        note: ''
      });
      fetchAds();
    } catch (error) {
      console.error("Error adding/updating ad invoice: ", error);
      if (error.code === 'permission-denied') {
        setError("Permission denied. Please check if you're properly authenticated.");
      } else {
        setError(`Failed to add/update ad invoice: ${error.message}`);
      }
    }
  };

  const handleEdit = (ad) => {
    setNewAd({
      date: ad.date,
      amount: ad.amount.toString(),
      note: ad.note
    });
    setEditingId(ad.id);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this ad invoice?")) {
      try {
        await deleteDoc(doc(db, 'Ads', id));
        console.log("Ad invoice deleted with ID: ", id);
        setSuccess("Ad invoice deleted successfully!");
        fetchAds();
      } catch (error) {
        console.error("Error deleting ad invoice: ", error);
        setError(`Failed to delete ad invoice: ${error.message}`);
      }
    }
  };

  const styles = {
    container: {
      maxWidth: '1000px',
      margin: '0 auto',
      padding: '24px',
      backgroundColor: '#f8fafc',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    header: {
      color: '#1e293b',
      fontSize: '24px',
      fontWeight: '600',
      marginBottom: '24px',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
    },
    form: {
      display: 'grid',
      gap: '16px',
      marginBottom: '32px',
      padding: '24px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    formGroup: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      position: 'relative',
    },
    formIcon: {
      position: 'absolute',
      left: '12px',
      color: '#64748b',
    },
    input: {
      width: '100%',
      padding: '10px 12px 10px 40px',
      fontSize: '14px',
      border: '1px solid #e2e8f0',
      borderRadius: '6px',
      transition: 'border-color 0.2s ease',
      '&:focus': {
        outline: 'none',
        borderColor: '#3b82f6',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.1)',
      },
    },
    button: {
      padding: '10px 20px',
      backgroundColor: '#4CAF50',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '16px',
    },
    table: {
      width: '100%',
      backgroundColor: 'white',
      borderRadius: '8px',
      overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    th: {
      backgroundColor: '#f8fafc',
      padding: '12px 16px',
      textAlign: 'left',
      color: '#64748b',
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    td: {
      padding: '12px 16px',
      borderBottom: '1px solid #e2e8f0',
      color: '#1e293b',
      fontSize: '14px',
    },
    actionButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      backgroundColor: '#ffffff',
      color: '#1e293b',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      '&:hover': {
        backgroundColor: '#f8fafc',
        transform: 'translateY(-1px)',
      },
      exportButton: {
        backgroundColor: '#3b82f6',
        color: 'white',
        border: 'none',
        '&:hover': {
          backgroundColor: '#2563eb',
        },
      },
      importButton: {
        backgroundColor: '#10b981',
        color: 'white',
        border: 'none',
        '&:hover': {
          backgroundColor: '#059669',
        },
      },
      sampleButton: {
        backgroundColor: '#6366f1',
        color: 'white',
        border: 'none',
        '&:hover': {
          backgroundColor: '#4f46e5',
        },
      },
    },
    tableButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      padding: '6px 12px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    editButton: {
      backgroundColor: '#fef3c7',
      color: '#92400e',
      border: 'none',
      '&:hover': {
        backgroundColor: '#fde68a',
      },
    },
    deleteButton: {
      backgroundColor: '#fee2e2',
      color: '#b91c1c',
      border: 'none',
      '&:hover': {
        backgroundColor: '#fecaca',
      },
    },
    submitButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '12px 24px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      '&:hover': {
        backgroundColor: '#2563eb',
      },
    },
    totals: {
      display: 'flex',
      gap: '24px',
      marginBottom: '24px',
      padding: '16px',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    },
    totalLabel: {
      color: '#64748b',
      fontSize: '14px',
      fontWeight: '500',
    },
    totalValue: {
      color: '#1e293b',
      fontSize: '16px',
      fontWeight: '600',
    },
    buttonContainer: {
      display: 'flex',
      gap: '12px',
      marginBottom: '24px',
      flexWrap: 'wrap',
    },
    buttonFile: {
      padding: '10px 20px',
      backgroundColor: '#4CAF50',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '16px',
    },
    fileInput: {
      display: 'none',
    }
  };

  return (
    <div style={styles.container}>
      <h2 style={styles.header}>
        <Receipt size={24} />
        Ad Invoice Management
      </h2>
      
      {error && <div style={{color: '#dc2626', marginBottom: '16px', padding: '12px', backgroundColor: '#fee2e2', borderRadius: '6px'}}>{error}</div>}
      {success && <div style={{color: '#059669', marginBottom: '16px', padding: '12px', backgroundColor: '#d1fae5', borderRadius: '6px'}}>{success}</div>}
      
      <div style={styles.buttonContainer}>
        <button
          onClick={exportAllToCsv}
          style={{...styles.actionButton, ...styles.exportButton}}
          disabled={isUploading}
        >
          <FileDown size={18} />
          Export All to CSV
        </button>
        <button
          onClick={() => fileInputRef.current?.click()}
          style={{...styles.actionButton, ...styles.importButton}}
          disabled={isUploading}
        >
          <FileUp size={18} />
          Import Bulk CSV
        </button>
        <button
          onClick={downloadCsvSample}
          style={{...styles.actionButton, ...styles.sampleButton}}
          disabled={isUploading}
        >
          <FileText size={18} />
          Download Sample
        </button>
        <input
          type="file"
          ref={fileInputRef}
          style={styles.fileInput}
          accept=".csv"
          onChange={handleBulkImport}
        />
      </div>

      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.formGroup}>
          <Calendar size={18} style={styles.formIcon} />
          <input
            type="date"
            name="date"
            value={newAd.date}
            onChange={handleInputChange}
            style={styles.input}
            required
          />
        </div>
        <div style={styles.formGroup}>
          <DollarSign size={18} style={styles.formIcon} />
          <input
            type="number"
            name="amount"
            value={newAd.amount}
            onChange={handleInputChange}
            placeholder="Ad Amount"
            style={styles.input}
            required
          />
        </div>
        <div style={styles.formGroup}>
          <FileInput size={18} style={styles.formIcon} />
          <textarea
            name="note"
            value={newAd.note}
            onChange={handleInputChange}
            placeholder="Note"
            style={{...styles.input, height: '100px', paddingLeft: '40px'}}
          />
        </div>
        <button type="submit" style={styles.submitButton}>
          {editingId ? (
            <>
              <Save size={18} />
              Update Ad Invoice
            </>
          ) : (
            <>
              <Plus size={18} />
              Add Ad Invoice
            </>
          )}
        </button>
      </form>

      <div style={styles.totals}>
        <div style={styles.totalItem}>
          <FileText size={18} color="#64748b" />
          <span style={styles.totalLabel}>Total Invoices:</span>
          <span style={styles.totalValue}>{ads.length}</span>
        </div>
        <div style={styles.totalItem}>
          <DollarSign size={18} color="#64748b" />
          <span style={styles.totalLabel}>Total Amount:</span>
          <span style={styles.totalValue}>${totalAmount.toFixed(2)}</span>
        </div>
      </div>

      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Invoice ID</th>
            <th style={styles.th}>Date</th>
            <th style={styles.th}>Amount</th>
            <th style={styles.th}>Note</th>
            <th style={styles.th}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {ads.map((ad) => (
            <tr key={ad.id}>
              <td style={styles.td}>{ad.id}</td>
              <td style={styles.td}>{ad.date}</td>
              <td style={styles.td}>${ad.amount.toFixed(2)}</td>
              <td style={styles.td}>{ad.note}</td>
              <td style={styles.td}>
                <div style={{display: 'flex', gap: '8px'}}>
                  <button 
                    onClick={() => handleEdit(ad)} 
                    style={{...styles.tableButton, ...styles.editButton}}
                  >
                    <Edit2 size={14} />
                    Edit
                  </button>
                  <button 
                    onClick={() => handleDelete(ad.id)} 
                    style={{...styles.tableButton, ...styles.deleteButton}}
                  >
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default Ads;